// Kaching cart helpers: move a bundle line to another deal bar (e.g. 3 → 6 months)
// with the free gifts Kaching would add, and find gift lines whose bundle was removed.
(() => {
  const PROPERTY = '__kaching_bundles';

  function parseBundle(line) {
    const raw = line?.properties?.[PROPERTY];
    if (!raw) return null;
    try {
      const bundle = JSON.parse(raw);
      return bundle?.deal ? bundle : null;
    } catch (_error) {
      return null;
    }
  }

  function parseJson(script) {
    try {
      return JSON.parse(script.textContent);
    } catch (_error) {
      return null;
    }
  }

  function numericId(gid) {
    const match = String(gid ?? '').match(/(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  async function loadProductPage(line) {
    const url = new URL(line.url, window.location.origin);
    url.searchParams.delete('variant');
    const response = await fetch(url.toString(), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Kaching config could not be loaded (${response.status}).`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function findDeal(doc, dealId) {
    return [...doc.querySelectorAll('script.kaching-bundles-deal-block-settings')]
      .map(parseJson)
      .find((config) => config?.nanoId === dealId) || null;
  }

  function productVariants(doc) {
    const variants = new Map();
    doc.querySelectorAll('script.kaching-bundles-product').forEach((script) => {
      const product = parseJson(script);
      if (!product?.id) return;
      const available = (product.variants || []).find((variant) => variant.availableForSale !== false);
      variants.set(Number(product.id), product.selectedVariantId || available?.id || null);
    });
    return variants;
  }

  function findBar(deal, variantId) {
    const barIds = new Set((deal.dealBars || []).map((bar) => bar.id));
    const mapping = (deal.defaultVariantsV2 || []).find((entry) => (
      barIds.has(entry.dealBarId) && (entry.variantGIDs || []).map(numericId).includes(variantId)
    ));
    return mapping ? deal.dealBars.find((bar) => bar.id === mapping.dealBarId) : null;
  }

  async function prepare(sourceLine, targetVariantId, { subscribed = false } = {}) {
    const bundle = parseBundle(sourceLine);
    if (!bundle) return null;

    const doc = await loadProductPage(sourceLine);
    const deal = findDeal(doc, bundle.deal);
    const bar = deal && findBar(deal, targetVariantId);
    if (!bar) return null;

    const variants = productVariants(doc);
    const gifts = (bar.freeGifts || [])
      .filter((gift) => gift.productGID && (!gift.applyOnlyForSubscriptions || subscribed))
      .map((gift) => ({
        id: numericId(gift.variantGIDs?.[0]) || variants.get(numericId(gift.productGID)),
        quantity: gift.quantity || 1,
        properties: {
          [PROPERTY]: JSON.stringify({ id: bundle.id, deal: bundle.deal, v: bundle.v, gift: gift.id })
        }
      }))
      .filter((gift) => gift.id);

    return {
      mainProperties: { ...sourceLine.properties, [PROPERTY]: JSON.stringify({ ...bundle, bar: bar.id }) },
      gifts
    };
  }

  function orphanedGiftUpdates(cart) {
    const bundles = (cart?.items || []).map((line) => ({ line, bundle: parseBundle(line) }));
    const mainBundleIds = new Set(bundles.filter(({ bundle }) => bundle?.main).map(({ bundle }) => bundle.id));
    return bundles
      .filter(({ bundle }) => bundle?.gift && !mainBundleIds.has(bundle.id))
      .reduce((updates, { line }) => ({ ...updates, [line.key]: 0 }), {});
  }

  window.CloudlessKachingUpgrade = { prepare, orphanedGiftUpdates };
})();
